<template>
    <div :class="{'table-of-content': true, 'table-of-content--is-page-loaded': isPageLoaded}">
        <template v-if="prismicDocument?.data.showTableOfContent !== false">
            <div class="top-row">
                <h3>{{ t('resources.tableOfContents') }}</h3>
                <VJoyIconButton
                    v-if="collapsible"
                    icon="arrow-right"
                    size="xsmall"
                    variant="secondary"
                    :class="{'toggle-button': true, open: isOpen}"
                    @click="toggleStickyMenu"
                />
            </div>
            <div :class="{'table-of-content-items': true, collapsible: collapsible, collapsed: collapsible && !isOpen}">
                <a
                    v-for="(title, index) of titles"
                    :key="`table-of-content-item-${index}`"
                    :href="`#${title.id}`"
                    :class="{[title.content.type]: true, active: activeAnchor === `#${title.id}`}"
                >
                    {{ asText([title.content]) }}
                </a>
            </div>
        </template>
        <SocialSharing v-if="!collapsible" :class="{'table-social-sharing': prismicDocument?.data.showTableOfContent !== false}" />
    </div>
</template>

<script setup lang="ts">
    import {inject, onBeforeUnmount, onMounted, ref} from 'vue';
    import {asText} from '@prismicio/helpers';
    import type {FilledContentRelationshipField, Slice} from '@prismicio/types';
    import type {ArticleDocument, ArticleDocumentData, EventDocument, GuideDocument} from '@prismicio-types';
    import {useRoute} from '@typed-router';
    import {useSlicesTitles, useTranslation} from '#imports';
    import {VJoyIconButton} from '@maltjoy/core-vue';
    import {PRISMIC_DOCUMENT} from '~/app/keys';
    import SocialSharing from '~/components/SocialSharing.vue';

    const props = defineProps<{
        document: EventDocument | ArticleDocument | GuideDocument | FilledContentRelationshipField<'article', string, ArticleDocumentData>;
        collapsible?: boolean;
    }>();

    // Composables
    const {getSlicesTitles} = useSlicesTitles();

    // Data
    const prismicDocument = inject(PRISMIC_DOCUMENT);
    const {t} = useTranslation();
    const {hash} = useRoute();
    const activeAnchor = ref<string>();
    const isOpen = ref(false);
    const slices = props.document.data?.slices as Slice[];
    const titles = getSlicesTitles(slices);

    function toggleStickyMenu() {
        isOpen.value = !isOpen.value;
    }

    function handleScroll() {
        const titleElements = titles.map((title) => document.getElementById(title.id as string));
        const activeTitle = titleElements.reduce(
            (prev, title) =>
                !prev ||
                (title?.getBoundingClientRect().top &&
                    title.getBoundingClientRect().top < 100 &&
                    title.getBoundingClientRect().top > prev.getBoundingClientRect().top)
                    ? title
                    : prev,
            null,
        );
        if (activeTitle) {
            activeAnchor.value = `#${activeTitle.id}`;
        }
    }

    const isPageLoaded = ref(false);

    onMounted(() => {
        isPageLoaded.value = true;
        activeAnchor.value = hash;
        window.addEventListener('scroll', handleScroll);
    });

    onBeforeUnmount(() => {
        window.removeEventListener('scroll', handleScroll);
    });
</script>

<style lang="scss">
    @use '~/assets/styles/variables' as *;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .table-of-content {
        background-color: var(--joy-color-neutral-0);
        width: 100%;
        border: solid 1px var(--joy-color-neutral-30);
        border-radius: var(--joy-core-radius-3);
        position: sticky;
        top: 92px;

        @include mq.screen_md(min) {
            opacity: 0;
            transition: opacity var(--joy-transition-duration-default) var(--joy-transition-timing-function);

            &--is-page-loaded {
                opacity: 1;
            }
        }

        @media (max-width: $mobile-breakpoint) {
            position: static;
            width: 100%;
        }
        .top-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            .toggle-button {
                --button-color-secondary: var(--joy-color-primary-50);
                --button-bg-color-secondary-hover: var(--joy-color-primary-10);
                margin-right: var(--joy-core-spacing-6);
                transform: rotate(-90deg);
                transition: transform var(--joy-transition-duration-default) var(--joy-transition-timing-function);
                &.open {
                    transform: rotate(90deg);
                }
            }
        }
        h3 {
            margin: var(--joy-core-spacing-6);
            line-height: 100%;
            font-weight: 800;
            @media (max-width: $mobile-breakpoint) {
                font-size: var(--joy-font-size-primary-600);
            }
        }

        .table-of-content-items {
            border-top: solid 1px var(--joy-color-neutral-30);

            display: flex;
            flex-direction: column;
            padding: var(--joy-core-spacing-6);
            gap: var(--joy-core-spacing-6);
            max-height: 100%;
            &.collapsible {
                overflow: auto;
                max-height: 50vh;
                transition: max-height var(--joy-transition-duration-default) var(--joy-transition-timing-function);
                &.collapsed {
                    max-height: 0;
                    overflow: hidden;
                    padding: 0 var(--joy-core-spacing-6);
                }
            }
            a {
                text-decoration: none;
                color: var(--joy-color-neutral-70);
                &.active {
                    color: var(--joy-color-primary-50);
                    position: relative;
                    &::after {
                        content: '';
                        position: absolute;
                        top: calc(0px - var(--joy-core-spacing-4));
                        bottom: calc(0px - var(--joy-core-spacing-4));
                        left: calc(0px - var(--joy-core-spacing-6));
                        width: 4px;
                        background-color: var(--joy-color-primary-50);
                        border-top-right-radius: var(--joy-core-radius-2);
                        border-bottom-right-radius: var(--joy-core-radius-2);
                    }
                }
            }
            .heading2 {
                font-weight: var(--joy-font-weight-bold);
                font-size: var(--joy-font-size-primary-400);
            }
            .heading3 {
                font-weight: var(--joy-font-weight-bold);
                font-size: var(--joy-font-size-primary-300);
                margin-left: var(--joy-core-spacing-3);
                &.active::after {
                    left: calc(0px - var(--joy-core-spacing-6) - var(--joy-core-spacing-3));
                }
            }
        }
        .table-social-sharing {
            border-top: solid 1px var(--joy-color-neutral-30);
        }
    }
</style>
